/**
 * New lead — one form on a desktop, two steps on a phone.
 *
 * The design splits it at 760px, and the split is where the form already had a
 * divider: the company on the first step, the optional first contact on the
 * second. Not an arbitrary halving — step one is everything required to create
 * the record, so the second step is genuinely skippable rather than a gate.
 *
 * On a phone the whole form is nine fields and a dropdown, which puts the
 * Create button below the fold and the keyboard over the top of it. Two steps
 * keep the action in sight of the fields it acts on.
 */
import { useState } from 'react'
import { Modal } from '@/shared/ui/Modal'
import { useIsMobile } from '@/shared/nav/useIsMobile'
import type { Contact, PipelineOutcome, PipelineStage } from '@/types/crm'
import type { NewLeadInput } from '../repository/leadsRepository'

interface Props {
  stages: (PipelineStage | PipelineOutcome)[]
  defaultOwner: string
  onClose: () => void
  onCreate: (input: NewLeadInput) => Promise<void>
}

export function NewLeadModal({ stages, defaultOwner, onClose, onCreate }: Props) {
  const [name, setName] = useState('')
  const [region, setRegion] = useState('')
  const [owner, setOwner] = useState(defaultOwner)
  const [stage, setStage] = useState(stages[0]?.key ?? 'prospect')
  const [contactName, setContactName] = useState('')
  const [contactEmail, setContactEmail] = useState('')
  const [contactPhone, setContactPhone] = useState('')
  const [skidaUser, setSkidaUser] = useState(false)
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const isMobile = useIsMobile()
  const [step, setStep] = useState<1 | 2>(1)

  const canSubmit = name.trim().length > 0 && !saving
  // On a desktop both halves are on screen at once, so "this step is showing"
  // is always true for both.
  const showCompany = !isMobile || step === 1
  const showContact = !isMobile || step === 2

  const submit = async () => {
    if (!canSubmit) return
    setSaving(true)
    setError(null)
    try {
      const contact: Contact | undefined =
        contactName.trim() || contactEmail.trim()
          ? {
              name: contactName.trim(),
              role: '',
              email: contactEmail.trim().toLowerCase(),
              phone: contactPhone.trim(),
              knownSkidaUser: skidaUser,
            }
          : undefined
      await onCreate({ name, region, owner, stage, contact })
      onClose()
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not create the lead.')
      setSaving(false)
    }
  }

  return (
    <Modal
      title="New lead"
      subtitle={
        isMobile
          ? `Add a company to the pipeline. · Step ${step} of 2`
          : 'Add a company to the pipeline.'
      }
      width={460}
      onClose={onClose}
      footer={
        isMobile && step === 1 ? (
          <>
            <button className="btn btn--ghost" onClick={onClose} disabled={saving}>
              Cancel
            </button>
            {/* Guarded by the same condition as Create. Letting you reach step
                two without a company name only moves the refusal one screen
                further from the field that causes it. */}
            <button
              className="btn btn--primary"
              onClick={() => setStep(2)}
              disabled={!name.trim()}
            >
              Next
            </button>
          </>
        ) : (
          <>
            <button
              className="btn btn--ghost"
              onClick={() => (isMobile ? setStep(1) : onClose())}
              disabled={saving}
            >
              {isMobile ? 'Back' : 'Cancel'}
            </button>
            <button className="btn btn--primary" onClick={submit} disabled={!canSubmit}>
              {saving ? 'Creating…' : 'Create lead'}
            </button>
          </>
        )
      }
    >
      {error && <div className="modal__error">{error}</div>}

      {showCompany && (
        <>
      <label className="field">
        <span>Company name</span>
        <input
          className="input"
          value={name}
          onChange={(e) => setName(e.target.value)}
          placeholder="e.g. Nordheim Adventures"
          autoFocus
        />
      </label>

      <div className="modal__grid2">
        <label className="field">
          <span>Region</span>
          <input
            className="input"
            value={region}
            onChange={(e) => setRegion(e.target.value)}
            placeholder="e.g. Lofoten"
          />
        </label>
        <label className="field">
          <span>Owner</span>
          <input
            className="input"
            value={owner}
            onChange={(e) => setOwner(e.target.value)}
            placeholder="e.g. Mari Solheim"
          />
        </label>
      </div>

      <label className="field">
        <span>Stage</span>
        <select value={stage} onChange={(e) => setStage(e.target.value)}>
          {stages.map((s) => (
            <option key={s.key} value={s.key}>
              {s.label}
            </option>
          ))}
        </select>
      </label>

        </>
      )}

      {showContact && (
        <>
      <div className="modal__divider">First contact (optional)</div>

      <div className="modal__grid2">
        <input
          className="input"
          value={contactName}
          onChange={(e) => setContactName(e.target.value)}
          placeholder="Contact name"
        />
        <input
          className="input"
          value={contactPhone}
          onChange={(e) => setContactPhone(e.target.value)}
          placeholder="Phone"
        />
      </div>
      <input
        className="input"
        value={contactEmail}
        onChange={(e) => setContactEmail(e.target.value)}
        placeholder="Email"
      />
      <label className="modal__check">
        <input
          type="checkbox"
          checked={skidaUser}
          onChange={(e) => setSkidaUser(e.target.checked)}
        />
        This contact already uses Skida (consumer)
      </label>
        </>
      )}
    </Modal>
  )
}
